import React, {type ReactNode} from 'react';
import {
  ThemeProvider,
  SizeContextProvider,
  useThemeContext,
} from '@packages/utilities/styled';
import type {
  SpringProviderUnits,
  SizeContextSizeValue,
  ThemeProviderProps,
} from '@packages/utilities/styled';
import {ToastProvider} from '../Toast';
import {SpringOverlay} from './SpringOverlay';
import {PresenceOutlineGradients} from '../Avatar/AvatarCircleOutline/PresenceOutlineGradients';

export type SpringProviderProps = {
  /**
   * Content for `SpringProvider`.
   */
  children?: ReactNode;
  /**
   * id used for Jest and Cypress test identification.
   */
  ['data-automation-id']?: string;
  /**
   * Determines if the theme tokens should be rendered at the `:root` level (the `<html>` element).
   * Otherwise, the theme tokens will be scoped to the `SpringProvider` component's HTML element.
   * Defaults to `false`.
   */
  setRoot?: boolean;
  /**
   * The size context.
   */
  size?: SizeContextSizeValue;
  /**
   * The desired theme.
   */
  theme?: ThemeProviderProps['theme'];
  /**
   * Determines if the base background and text colors should be rendered by `ThemeProvider`.
   */
  setBackgroundAndColorOnRoot?: boolean;
  /**
   * Conditionally renders the `SpringOverlay` component.
   */
  shouldNotMountOverlay?: boolean;
  /**
   * A TEMPORARY prop to disable the `ToastProvider`. Supplied for Marketplace migration. DO NOT USE OTHERWISE.
   */
  shouldUseToastProvider?: boolean;
  /**
   * Optional top offset for top-anchored Spring toasts on non-mobile
   * viewports. Written verbatim into an inline `--spring-toast-top` custom
   * property on the toast container. Mobile stays at `top: 0`.
   */
  toastOffsetTop?:
    | `${number}px`
    | `${number}rem`
    | `calc(${string})`
    | `min(${string})`
    | `max(${string})`
    | `clamp(${string})`;
  /**
   * Optional right offset for the `top-right` Spring toast container on
   * non-mobile viewports. Written verbatim into an inline
   * `--spring-toast-right` custom property. Mobile stays full-width.
   */
  toastOffsetRight?:
    | `${number}px`
    | `${number}rem`
    | `calc(${string})`
    | `min(${string})`
    | `max(${string})`
    | `clamp(${string})`;
  /**
   * The units context.
   * @deprecated
   */
  units?: SpringProviderUnits;
  /**
   * Adds core CSS colors into the Theme wrapper div.
   * This is useful when we are rendering Spring somewhere we don't want to inject our core colors onto root (e.g. Editor).
   */
  _addCoreColorsOnTheme?: boolean;
};

/**
 * @component
 */
export const SpringProvider = ({
  children,
  size = 'x-large',
  shouldUseToastProvider = true,

  // This is so we can disable the Spring Overlay injection for Designer.
  shouldNotMountOverlay = false,

  toastOffsetTop,
  toastOffsetRight,
  ...rest
}: SpringProviderProps) => {
  const themeContextValue = useThemeContext();
  const isRootProvider = themeContextValue.isRootProvider === null;

  const content = shouldUseToastProvider ? (
    <ToastProvider
      toastOffsetRight={toastOffsetRight}
      toastOffsetTop={toastOffsetTop}
    >
      {children}
    </ToastProvider>
  ) : (
    children
  );
  return (
    <ThemeProvider {...rest}>
      <SizeContextProvider size={size}>
        <SpringOverlay shouldNotMountOverlay={shouldNotMountOverlay}>
          {isRootProvider && <PresenceOutlineGradients />}
          {content}
        </SpringOverlay>
      </SizeContextProvider>
    </ThemeProvider>
  );
};

SpringProvider.displayName = 'SpringProvider';

export default SpringProvider;
